"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, SearchCode, Globe as Globe2, Calendar } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
export default function RelatedServicesCta() {
  const prefersReducedMotion = useReducedMotion();
  const services = [{
    title: "SEO Audits",
    to: "/SeoAuditsPage",
    badge: "Diagnostic & Roadmapping",
    icon: SearchCode,
    description: "A comprehensive technical and on-page review identifying crawl roadblocks, indexation leaks, and untapped ranking opportunities.",
    points: ["In-depth technical architecture audit", "Actionable prioritization matrix", "Direct review meeting with me"]
  }, {
    title: "National SEO",
    to: "/NationalSeoPage",
    badge: "High-Intent Scale",
    icon: Globe2,
    description: "Organic growth campaigns built to capture high-value commercial searches across the UK and outrank established competitors.",
    points: ["Strategic commercial keyword targeting", "Content cluster & authority building", "Transparent monthly performance reporting"]
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedservicescta" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {/* Subtle brand ambient accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-secondary/40 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-1/4 h-96 w-96 rounded-full bg-secondary/30 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-14 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide">
            Complementary Strategies
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold font-default tracking-tight text-foreground">
            Explore related SEO solutions
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
            Explore targeted technical audits and national search solutions designed to scale your specialist sector visibility.
          </p>
        </div>

        {/* Related Service Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {services.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card text-card-foreground shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 flex flex-col justify-between">
                  <div>
                    <CardHeader className="space-y-3 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <IconComponent className="size-5" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground tracking-wide">
                          {service.badge}
                        </span>
                      </div>
                      <CardTitle className="text-2xl font-bold font-default pt-2">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-sm font-sans text-muted-foreground leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <ul className="space-y-2.5">
                        {service.points.map((point, pointIndex) => <li key={pointIndex} className="flex items-start text-sm text-foreground/80 font-sans gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>
                  <div className="px-6 pb-6 pt-4 mt-auto">
                    <Button asChild variant="outline" className="w-full justify-between group font-medium">
                      <Link to={service.to}>
                        <span>Explore {service.title}</span>
                        <ArrowRight className="size-4 text-primary transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Final Consultation Call-out Box */}
        <motion.div initial={{
        opacity: 0,
        y: prefersReducedMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="max-w-4xl mx-auto rounded-2xl border border-primary/20 bg-muted/40 p-8 md:p-12 text-center shadow-xs">
          <div className="inline-flex size-12 rounded-full bg-primary/10 text-primary items-center justify-center mb-5">
            <SafeHydrate><Calendar className="size-6" /></SafeHydrate>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground tracking-tight">
            Ready to build sustainable search traffic?
          </h3>
          <p className="mt-3 text-sm md:text-base text-muted-foreground font-sans max-w-xl mx-auto leading-relaxed">
            Book a direct consultation to explore these related SEO services and map out a compliant search roadmap with me.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto px-8 py-6 text-base font-semibold shadow-sm">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-6 py-6 text-base font-medium">
              <Link to="/CaseStudiesPage">
                View Verified Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}